import { lazy, Suspense, useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { Modal } from "../../ui/Modal";
import { portalReportsAdminApi, type ProcedureParameter } from "../../../api/portalReportsAdminApi";

// CodeMirror is only needed on the admin route; keep it out of the main bundle.
const SqlViewer = lazy(() => import("./SqlViewer"));

/** Read-only source of a report procedure (no editing from the portal). */
export function ProcedureSourceModal({ name, parameters, onClose }: { name: string | null; parameters?: ProcedureParameter[]; onClose: () => void }) {
  const [source, setSource] = useState<string | null>(null);
  const [modifyDate, setModifyDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!name) return;
    let active = true;
    setSource(null);
    setError(null);
    setCopied(false);
    portalReportsAdminApi
      .procedureSource(name)
      .then((r) => {
        if (!active) return;
        setSource(r.definition);
        setModifyDate(r.modifyDate);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "Failed to load the procedure"));
    return () => {
      active = false;
    };
  }, [name]);

  const copy = async () => {
    if (!source) return;
    try {
      await navigator.clipboard.writeText(source);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal
      open={!!name}
      onClose={onClose}
      title={name ? `dbo.${name}` : "Procedure"}
      maxWidth={1100}
      footer={
        <>
          <span style={{ marginRight: "auto", fontSize: "0.8rem", color: "var(--portal-muted)" }}>
            Read-only. Procedures are changed by the team via SQL scripts.{modifyDate ? ` Last modified ${modifyDate.slice(0, 16).replace("T", " ")} UTC.` : ""}
          </span>
          <button type="button" className="btn btn-secondary" onClick={() => void copy()} disabled={!source} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Copy size={14} /> {copied ? "Copied" : "Copy"}
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {parameters && parameters.length > 0 && (
          <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
            Parameters: {parameters.map((p) => `@${p.name} ${p.sqlType}${p.hasDefault ? " (optional)" : ""}`).join(", ")}
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}
        {source == null && !error ? (
          <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
        ) : source != null ? (
          <Suspense fallback={<pre style={{ fontSize: "0.8rem", maxHeight: 520, overflow: "auto" }}>{source}</pre>}>
            <SqlViewer value={source} />
          </Suspense>
        ) : null}
      </div>
    </Modal>
  );
}
